/**
 * KAN-898: "В мусор" и "Вернуть" для контакта. Один хук для карточки кабинета,
 * карточки приложения менеджера и строки таблицы CRM: запрос, тост, обновление
 * списков (CRM, внимание, воронка, контакты, теплота лидов, менеджеры) и колбэк после успеха.
 */
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { clientsApi } from '@/services/api/clients';
import { clientQueryKey } from '@/services/api/clientQueries';
import {
  ATTENTION_QUERY_KEY,
  DASHBOARD_FUNNEL_QUERY_KEY,
  LEAD_TEMPERATURE_QUERY_KEY,
  MANAGERS_QUERY_KEY,
} from '@/services/api/queryKeys';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { ANY_UUID } from '@/lib/managerAccess';

export type ClientJunkAction = 'junk' | 'unjunk';

/**
 * Адрес действия для проверки доступа (useCanCallApi). Правило api_access не
 * зависит от клиента, поэтому проверка по образцу UUID (id в тестах и старых
 * данных бывает не UUID).
 */
export const clientJunkAccessUrl = (action: ClientJunkAction): string => `/api/v1/clients/${ANY_UUID}/${action}/`;

const TEXT: Record<ClientJunkAction, { done: string; failed: string }> = {
  junk: { done: 'Контакт убран в мусор', failed: 'Не удалось убрать в мусор' },
  unjunk: { done: 'Контакт возвращён', failed: 'Не удалось вернуть контакт' },
};

/**
 * Обновить всё, что показывает контакт, после действия над ним (мусор, KAN-924
 * "Вернуть в работу"): внимание и все списки CRM (вложены в ATTENTION_QUERY_KEY),
 * воронку, контакты, карточку клиента, теплоту лидов, счётчики по менеджерам.
 */
export function invalidateClientLists(queryClient: QueryClient, clientId: string): void {
  void queryClient.invalidateQueries({ queryKey: ATTENTION_QUERY_KEY });
  void queryClient.invalidateQueries({ queryKey: DASHBOARD_FUNNEL_QUERY_KEY });
  void queryClient.invalidateQueries({ queryKey: ['clients'] });
  // Карточка клиента (метки и кнопки зависят от junk_at и outcome).
  void queryClient.invalidateQueries({ queryKey: clientQueryKey(clientId) });
  void queryClient.invalidateQueries({ queryKey: LEAD_TEMPERATURE_QUERY_KEY });
  void queryClient.invalidateQueries({ queryKey: MANAGERS_QUERY_KEY });
}

export function useClientJunk(action: ClientJunkAction, onDone?: (clientId: string) => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) => (action === 'junk' ? clientsApi.junk(clientId) : clientsApi.unjunk(clientId)),
    onSuccess: (_client, clientId) => {
      toast.success(TEXT[action].done);
      invalidateClientLists(queryClient, clientId);
      onDone?.(clientId);
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, TEXT[action].failed));
    },
  });
}
